import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, Search, ShieldAlert, Briefcase } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Skeleton } from '@/components/ui/Skeleton'
import { ChannelBadge } from './components/ChannelBadge'
import { StatusChip } from './components/StatusChip'
import { listTransactions } from './api'
import { formatDateTime, formatNaira } from '@/lib/format'
import { cn } from '@/lib/cn'
import { useMe } from '@/features/auth/hooks'
import { listBranches } from '@/features/admin/branches/api'
import type {
  Channel,
  TransactionStatus,
  TransactionsListFilters,
  TransactionSummary,
} from './types'

const CHANNELS: Channel[] = [
  'nip',
  'pos',
  'rtgs',
  'ussd',
  'mobile_app',
  'internet_banking',
  'agent_banking',
  'atm',
]
const CHANNEL_LABEL: Record<Channel, string> = {
  pos: 'POS',
  nip: 'NIP',
  rtgs: 'RTGS',
  ussd: 'USSD',
  mobile_app: 'Mobile',
  internet_banking: 'Internet',
  agent_banking: 'Agent',
  atm: 'ATM',
}

const STATUSES: TransactionStatus[] = [
  'successful',
  'pending',
  'failed',
  'declined',
  'cancelled',
  'reversed',
  'on_hold',
]

/**
 * Transactions queue. Server-side filtered list driven by
 * `TransactionsListFilters`; the page mirrors the analyst-style filter
 * UX from alerts and SARs (chip groups + search + amount range).
 */
export function TransactionsListPage() {
  const [filters, setFilters] = useState<TransactionsListFilters>({})
  const [amountMin, setAmountMin] = useState<string>('')
  const [amountMax, setAmountMax] = useState<string>('')

  // Branch filter is only meaningful for HQ users — branch-scoped
  // analysts already see only their own branch (server-enforced),
  // so the chip group is hidden for them.
  const { data: me } = useMe()
  const branches = useQuery({
    queryKey: ['admin', 'branches', 'list'] as const,
    queryFn: () => listBranches(),
    enabled: me?.is_hq === true,
    staleTime: 60_000,
  })

  const effective = useMemo<TransactionsListFilters>(
    () => ({
      ...filters,
      amount_min_minor: parseAmountToMinor(amountMin),
      amount_max_minor: parseAmountToMinor(amountMax),
    }),
    [filters, amountMin, amountMax],
  )

  const list = useQuery({
    queryKey: ['transactions', 'list', effective] as const,
    queryFn: () => listTransactions(effective),
    staleTime: 15_000,
  })

  const items = list.data?.transactions ?? []

  const toggleChannel = (c: Channel) =>
    setFilters((cur) => ({
      ...cur,
      channel: (cur.channel ?? []).includes(c)
        ? (cur.channel ?? []).filter((x) => x !== c)
        : [...(cur.channel ?? []), c],
    }))

  const toggleStatus = (s: TransactionStatus) =>
    setFilters((cur) => ({
      ...cur,
      status: (cur.status ?? []).includes(s)
        ? (cur.status ?? []).filter((x) => x !== s)
        : [...(cur.status ?? []), s],
    }))

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-xl font-semibold">Transactions</h1>
        <p className="text-sm text-muted-fg">
          Posted and pending transactions across all channels. Open one to
          see the full payload, balances, and any linked alerts.
        </p>
      </header>

      <Card>
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="tx-q">Search</Label>
              <span className="relative inline-block">
                <Search
                  size={14}
                  aria-hidden
                  className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-fg"
                />
                <Input
                  id="tx-q"
                  value={filters.q ?? ''}
                  onChange={(e) =>
                    setFilters((c) => ({ ...c, q: e.target.value || undefined }))
                  }
                  placeholder="Reference or NUBAN"
                  className="w-72 pl-7"
                />
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="tx-amount-min">Amount min (₦)</Label>
              <Input
                id="tx-amount-min"
                inputMode="decimal"
                value={amountMin}
                onChange={(e) => setAmountMin(e.target.value)}
                placeholder="e.g. 5000"
                className="w-32"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="tx-amount-max">Amount max (₦)</Label>
              <Input
                id="tx-amount-max"
                inputMode="decimal"
                value={amountMax}
                onChange={(e) => setAmountMax(e.target.value)}
                placeholder="e.g. 1000000"
                className="w-32"
              />
            </div>
          </div>
          <FilterRow
            label="Channel"
            options={CHANNELS.map((c) => ({ value: c, label: CHANNEL_LABEL[c] }))}
            active={filters.channel ?? []}
            onToggle={(v) => toggleChannel(v as Channel)}
          />
          <FilterRow
            label="Status"
            options={STATUSES.map((s) => ({
              value: s,
              label: s.charAt(0).toUpperCase() + s.slice(1),
            }))}
            active={filters.status ?? []}
            onToggle={(v) => toggleStatus(v as TransactionStatus)}
          />
          {me?.is_hq && (branches.data?.branches?.length ?? 0) > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[0.6875rem] uppercase tracking-wider text-muted-fg">
                Branch:
              </span>
              <button
                type="button"
                onClick={() =>
                  setFilters((c) => ({ ...c, branch: undefined }))
                }
                className={
                  'rounded border px-2 py-0.5 text-[0.6875rem] ' +
                  (!filters.branch
                    ? 'border-primary bg-primary text-primary-fg'
                    : 'border-border bg-card text-muted-fg hover:border-primary')
                }
              >
                All
              </button>
              {branches.data!.branches.map((b) => (
                <button
                  key={b.public_id}
                  type="button"
                  onClick={() =>
                    setFilters((c) => ({ ...c, branch: b.public_id }))
                  }
                  className={
                    'rounded border px-2 py-0.5 text-[0.6875rem] ' +
                    (filters.branch === b.public_id
                      ? 'border-primary bg-primary text-primary-fg'
                      : 'border-border bg-card text-muted-fg hover:border-primary')
                  }
                  title={b.name}
                >
                  {b.code}
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {list.isLoading && (
        <div className="flex flex-col gap-1">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}
      {list.isError && (
        <p className="rounded-md border border-status-failed/30 bg-status-failed/5 p-3 text-sm text-status-failed">
          Couldn't load transactions.
        </p>
      )}
      {list.data && items.length === 0 && (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-fg">
          No transactions match the current filters.
        </p>
      )}

      <ul className="flex flex-col gap-1">
        {items.map((t) => (
          <li key={t.id}>
            <Row tx={t} />
          </li>
        ))}
      </ul>
    </div>
  )
}

function FilterRow({
  label,
  options,
  active,
  onToggle,
}: {
  label: string
  options: { value: string; label: string }[]
  active: readonly string[]
  onToggle: (v: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-16 shrink-0 text-xs uppercase tracking-wide text-muted-fg">
        {label}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const isActive = active.includes(o.value)
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={isActive}
              onClick={() => onToggle(o.value)}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs',
                isActive
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-fg hover:bg-muted',
              )}
            >
              {o.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function Row({ tx }: { tx: TransactionSummary }) {
  return (
    <Link
      to={`/transactions/${tx.id}`}
      className="grid grid-cols-[minmax(0,8rem)_auto_minmax(0,1fr)_auto_auto_auto_1.25rem] items-center gap-3 rounded-md border border-border bg-card px-3 py-2 text-sm transition-colors hover:border-primary/40"
    >
      <span className="font-mono text-xs">{tx.transaction_ref}</span>
      <ChannelBadge channel={tx.channel} />
      <span className="flex items-center gap-2 truncate text-xs">
        <span className="font-mono text-muted-fg">
          {tx.source_bank_short_name} · {tx.source_nuban}
        </span>
        <ChevronRight size={10} className="text-muted-fg" aria-hidden />
        <span className="font-mono text-muted-fg">
          {tx.destination_bank_short_name} · {tx.destination_nuban}
        </span>
      </span>
      <span className="font-mono text-sm tabular-nums">{formatNaira(tx.amount_minor)}</span>
      <StatusChip status={tx.status} />
      <span className="flex items-center gap-1 text-[0.65rem] text-muted-fg">
        {tx.branch_code && (
          <span
            className="inline-flex items-center gap-0.5 rounded bg-primary/10 px-1.5 py-0.5 font-mono text-primary"
            title={tx.branch_name ?? tx.branch_code}
          >
            {tx.branch_code}
          </span>
        )}
        {tx.has_alert && (
          <span className="inline-flex items-center gap-0.5 rounded bg-status-pending/10 px-1.5 py-0.5 text-status-pending">
            <ShieldAlert size={10} aria-hidden /> alert
          </span>
        )}
        {tx.has_case && (
          <span className="inline-flex items-center gap-0.5 rounded bg-status-info/10 px-1.5 py-0.5 text-status-info">
            <Briefcase size={10} aria-hidden /> case
          </span>
        )}
        <span className="ml-1">{formatDateTime(tx.occurred_at)}</span>
      </span>
      <ChevronRight size={14} className="text-muted-fg" aria-hidden />
    </Link>
  )
}

function parseAmountToMinor(input: string): number | undefined {
  if (input.trim() === '') return undefined
  const n = Number(input.replace(/,/g, ''))
  if (!Number.isFinite(n) || n <= 0) return undefined
  return Math.round(n * 100)
}
